import {
    DefaultFiltersContainerType,
    DefaultFiltersType,
    UserSettingsType,
} from 'store/auth/types';
import i18n from 'i18n';
import { t } from 'i18next';
import { FiltersType, FilterType } from 'store/global/types';

const getPrompt = (
    data: FilterType[],
    value?: number | string | (number | string)[],
    country_code?: string | null,
) => {
    if (Array.isArray(value)) {
        return value
            .map((v) => {
                const filterData = data.find((item) => item.id == v);
                if (filterData?.additional?.[country_code as string]) {
                    return filterData?.additional?.[country_code as string];
                }
                return filterData?.prompt || filterData?.value;
            })
            .filter(Boolean)
            .join(' , ');
    }

    const filterData = data.find(
        (item) =>
            String(item.value) === String(value) ||
            String(item.id) === String(value) ||
            +item.id === Number(value),
    );
    if (filterData?.additional?.[country_code as string]) {
        return filterData?.additional?.[country_code as string];
    }
    return filterData?.prompt || filterData?.value;
};

export const getFormattedFilters = (
    params: DefaultFiltersType | null,
    additionalSettings: FiltersType,
    country_code?: string | null,
) => {
    if (!params) {
        return;
    }
    const formalityPrompt = getPrompt(
        additionalSettings.FORMALITY_MARKS,
        params.formality,
        country_code,
    );
    const urgencyPrompt = getPrompt(additionalSettings.URGENCY_MARKS, params.urgency, country_code);
    const lengthPrompt = getPrompt(additionalSettings.LENGTH_MARKS, params.length, country_code);
    const structuresPrompt = getPrompt(
        additionalSettings.STRUCTURES,
        params.structures,
        country_code,
    );
    const toneOfVoicePrompt = getPrompt(
        additionalSettings.TONES_OF_VOICE,
        params.tone_of_voice,
        country_code,
    );

    const data = {
        formality: formalityPrompt,
        urgency: urgencyPrompt,
        length: lengthPrompt,
        anonymized: additionalSettings.ANONYMIZED[params.anonymized],
        instructions: params.instructions,
        structures: structuresPrompt,
        tone_of_voice: toneOfVoicePrompt,
    };
    return data;
};

export function getTranslatedLabels<T>(data: T, filterKey: string) {
    if (Array.isArray(data)) {
        const updatedData = data.map((item) => ({
            ...item,
            label: i18n.exists(`web_app.additional_settings.${filterKey}.${item.id}`)
                ? t(`web_app.additional_settings.${filterKey}.${item.id}`)
                : item.label,
        }));
        return updatedData;
    }
    return [];
}

export function getTranslatedLabelsSelect<T>(data: T, filterKey: string) {
    if (Array.isArray(data)) {
        const updatedData = data.map((item) => ({
            ...item,
            label: i18n.exists(`web_app.additional_settings.${filterKey}[${item.id}]`)
                ? t(`web_app.additional_settings.${filterKey}[${item.id}]`)
                : item.label,
        }));
        return updatedData;
    }
    return [];
}

export function getTranslatedDataLanguages<T>(data: T) {
    if (Array.isArray(data)) {
        const updatedData = data.map((item) => {
            return {
                ...item,
                language_name: i18n.exists(
                    `web_app.settings.output_languages.${item.language_code}`,
                )
                    ? t(`web_app.settings.output_languages.${item.language_code}`)
                    : item.language_name,
            };
        });
        return updatedData;
    }
    return [];
}

export function getTranslatedTargetCountries<T>(data: T) {
    if (Array.isArray(data)) {
        const updatedData = data
            .map((item) => {
                return {
                    ...item,
                    title: i18n.exists(`web_app.settings.target_countries.${item.country_code}`)
                        ? t(`web_app.settings.target_countries.${item.country_code}`)
                        : item.title,
                };
            })
            .sort((a, b) => a.title.localeCompare(b.title));
        return updatedData;
    }
    return [];
}

/**
 * Normalizes filters to handle backwards compatibility.
 * Converts old string-based tone_of_voice and string-based structures array to numeric format.
 * Requires additionalSettings to map string values to numeric IDs.
 */
export function normalizeFilters(
    filters: DefaultFiltersType,
    additionalSettings?: FiltersType,
): DefaultFiltersType {
    const normalizedFilters = { ...filters } as DefaultFiltersType & {
        tone_of_voice: number;
        structures: number[];
    };
    // Handle tone_of_voice: convert string to number if needed
    if (typeof filters?.tone_of_voice === 'string') {
        const numValue = parseInt(filters?.tone_of_voice, 10);
        if (!isNaN(numValue)) {
            normalizedFilters.tone_of_voice = numValue;
        } else if (additionalSettings?.TONES_OF_VOICE) {
            // Try to find by label or value
            const found = additionalSettings.TONES_OF_VOICE.find(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (t: any) => t.label === filters.tone_of_voice || t.value === filters.tone_of_voice,
            );
            normalizedFilters.tone_of_voice = found ? Number(found.id) : 1;
        } else {
            normalizedFilters.tone_of_voice = 1;
        }
    }

    // Handle structures: convert to array if string, then convert values to numeric IDs
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let structuresArray: any[] = [];
    if (Array.isArray(filters?.structures)) {
        structuresArray = filters.structures;
    } else if (typeof filters?.structures === 'string') {
        structuresArray = [filters.structures];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    normalizedFilters.structures = structuresArray.map((s: any): number => {
        // Handle string format (old DB stores the actual value text)
        if (typeof s === 'string') {
            if (additionalSettings?.STRUCTURES) {
                // Try to find by exact value match
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const found = additionalSettings.STRUCTURES.find((st: any) => st.value === s);
                if (found) {
                    return Number(found.id);
                }
            }
            // Fallback: try to parse as number
            const numValue = parseInt(s, 10);
            return !isNaN(numValue) ? numValue : 1;
        }
        // Already a number
        return s as number;
    });

    // Clean up old format properties if they exist
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = normalizedFilters as any;
    if (result.toneOfVoice !== undefined) {
        delete result.toneOfVoice;
    }

    return result as DefaultFiltersType;
}

export function getDefaultFilters(
    _userSettings: UserSettingsType | null,
    defaultFilters: DefaultFiltersContainerType,
) {
    /* Looks like some old, deprecated logic as I can't find traces of such logic in firestore */
    // if (
    //     userSettings?.brand === BRAND_TEMPO_TEAM &&
    //     userSettings.target_country_code === COUNTRY_CODE_NETHERLANDS
    // ) {
    //     return defaultFilters.items.find(
    //         (item) => item.id === FILTER_NETHERLAND_TEMO_TEAM,
    //     ) as DefaultFiltersType;
    // } else if (userSettings?.target_country_code === COUNTRY_CODE_INDIA) {
    //     return defaultFilters.items.find(
    //         (item) => item.id === COUNTRY_CODE_INDIA,
    //     ) as DefaultFiltersType;
    // }
    // return defaultFilters.items.find((item) => item.default) as DefaultFiltersType;
    const filters = defaultFilters.items[0];
    return normalizeFilters(filters);
}
